import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';

import { activityService } from '../../Services';

import Loader from '../Common/Loader';

import {ReactComponent as Mail} from '../../images/icons/Communication/mail-01.svg';
import {ReactComponent as Trash} from '../../images/icons/General/trash-01.svg';
import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';

export const ActivityItem = () => {
    const params = useParams();
    const [item, setItem] = useState(null);
    const [signups, setSignups] = useState([]);
    const [mySignupId, setMySignupId] = useState(null);
    const [allowEdit, setAllowEdit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const remember = useSelector((state) => state.org.remember);
    const userId = useSelector((state) => state[remember ? 'localUser' : 'user'].id);
    const permissions = useSelector((state) => state[remember ? 'localUser' : 'user'].permissions)?.split(',');
    const is24h = useSelector((state) => state[remember ? 'localUser' : 'user'].is24h);

    const loadItem = (id) => {
        setError('');
        setItem(null);
        setSignups([]);
        setMySignupId(null);
        activityService.activityItem(id).then(res => {
            if (res.error) {
                setError(res.error);
                return;
            }
            setItem(res.item);
            setSignups(res.signups);
            setMySignupId(res.mySignup ?? null);
            setAllowEdit(res.allowEdit)
        });
    };

    useEffect(() => {
        loadItem(params.id);
    }, [params.id]);

    const handleSignup = async (event) => {
        event.preventDefault();

        setLoading(true);
        setError('');
        const res = await activityService.activitySignup(item.id);
        if (res.error) {
            setError(res.error);
        } else {
            loadItem(params.id);
        }
        setLoading(false);
    };

    const handleUnsignup = async (event, id = 0) => {
        setLoading(id || mySignupId);
        setError('');
        const res = await activityService.activityUnsignup(id || mySignupId);
        if (res.error) {
            setError(res.error);
        } else {
            loadItem(params.id);
        }
        setLoading(false);
    };

    if (!item && !error) return <Loader />;
    if (!item && error) return <p className="error">{error}</p>;

    const isFull = item.maxSignups && signups.length >= item.maxSignups;
    const isPast = item.startDate && new Date(item.startDate) < new Date(new Date().toDateString());

    const formatTime = (str) => {
        if (!str) return '';
        const [h, m] = str.split(':');
        if (is24h) return `${String(+h).padStart(2, '0')}:${m}`;
        const hour = +h % 12 || 12;
        return `${hour}:${m} ${+h < 12 ? 'AM' : 'PM'}`;
    };

    return (
        <>
            <h1>{item.name || item.activityTypeLabel}</h1>

            <div className="bordered-content info-table content-820">
                {permissions.includes('Edit activity') ? 
                    <div className="header">
                        <div className="right">
                        <Link to={`/activities/${params.slug}/edit/${item.id}`}>
                            <button className="primary">Edit activity</button>
                        </Link>
                        </div>
                    </div> : ''
                }
                <table>
                    <tbody>
                        {item.description && (
                            <tr>
                                <th>Description</th>
                                <td>{item.description}</td>
                            </tr>
                        )}
                        <tr>
                            <th>Start date</th>
                            <td>{item.startDate}</td>
                        </tr>
                        {item.endDate && (
                            <tr>
                                <th>End date</th>
                                <td>{item.endDate}</td>
                            </tr>
                        )}
                        {item.startTime && (
                            <tr>
                                <th>Start time</th>
                                <td>{formatTime(item.startTime)}</td>
                            </tr>
                        )}
                        {item.endTime && (
                            <tr>
                                <th>End time</th>
                                <td>{formatTime(item.endTime)}</td>
                            </tr>
                        )}
                        <tr>
                            <th>Person in charge</th>
                            <td>{item.personInCharge}</td>
                        </tr>
                        
                        {item.hasLocation && item.location ? (
                            <tr>
                                <th>Location</th>
                                <td>{item.location}</td>
                            </tr>
                        ) : ''}
                        {item.hasCost && item.cost != null ? (
                            <tr>
                                <th>Cost</th>
                                <td>{item.cost}</td>
                            </tr>
                        ) : ''}
                        <tr>
                            <th>Maximum signups</th>
                            <td>{item.maxSignups}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="bordered-content content-820">
                <div className="header">
                    <h4 className="content-820">
                        Signups ({signups.length}{item.maxSignups ? ' / ' + item.maxSignups : ''})
                    </h4>
                    <div className="buttons">
                        {signups.length > 0 && (
                            <a href={`mailto:?cc=${signups.map(s => s.email).filter(Boolean).join(';')}`}>
                                <button className="btn primary"><Mail /> Email all</button>
                            </a>
                        )}
                    </div>
                </div>
                {signups.length ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th>
                                {allowEdit ? <th style={{width: '75px'}}>Delete</th> : ''}
                            </tr>
                        </thead>
                        <tbody>
                            {signups.map(s => (
                                <tr key={s.id}>
                                    <td><a href={`mailto:${s.email}`}>{s.lastName}, {s.firstName}</a></td>
                                    {allowEdit ? <td>
                                        {loading === s.id ? <Loading /> :
                                            <>
                                                <Trash className="desktop pointer" onClick={(event) => handleUnsignup(event, s.id)} />
                                                <button className="mobile label link" onClick={(event) => handleUnsignup(event, s.id)}>Delete</button>
                                            </>}
                                        </td> : ''}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="empty">No signups yet</p>
                )}
            </div>

            {userId ? (
                mySignupId ? (
                    <div className="bordered-content content-820">
                        <div className="header">
                            {error && <p className="error">{error}</p>}
                            <p>You are signed up for this activity.</p>
                            {!isPast && (
                                <p>
                                    <button onClick={handleUnsignup} disabled={loading} className="primary">
                                        {loading ? 'Processing...' : 'Unsubscribe'}
                                    </button>
                                </p>
                            )}
                        </div>
                    </div>
                ) : !isFull && !isPast ? (
                    <div className="bordered-content content-820">
                        <div className="header">
                            <h4>Sign up</h4>
                            {error && <p className="error">{error}</p>}
                            <form onSubmit={handleSignup}>
                                <div className="form-submit">
                                    <button disabled={loading} className="primary">
                                        {loading ? 'Processing...' : 'Sign Up'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : null
            ) : null}
        </>
    );
};

export default ActivityItem;
